import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TextInputComponent implements OnInit, ControlValueAccessor {
  @Input() public formCtrl: FormControl | undefined;

  @Input() public label: string = '';

  @Input() public placeholder: string = '';

  @Input() public hintMessage: string = '';

  @Input() public disabled: boolean = false;

  @Input() public type: string = 'text';

  @Input() public hideBorder: boolean = false;

  @Input() public showNumberButtons: boolean = false;

  @Input() public numberButtonsIconUp: string = 'arrow_drop_up';

  @Input() public numberButtonsIconDown: string = 'arrow_drop_down';

  @Input() public minNumber: number | undefined;

  @Input() public maxNumber: number | undefined;

  @Input() public initInput: string = '';

  @Input() public newDesign: boolean = false;

  @Input() public autoComplete: string = 'off';

  @Input() public attributeName: string | undefined;

  @Input() public isMaterialDesign: boolean = false;

  public isItPassword: boolean = false;

  public inputValue: string = '';

  /*
   ** errorMessagesObject contains key value pairs of possible errors,
   ** when 'key' represents the error name, 'value' represents the message to show when the error 'key' is present,
   */
  @Input() public errorMessagesObject: any = {};

  //iconType : 'image' / 'mat-icon'.
  @Input() public iconType: string = 'mat-icon';

  //Icon : button icon : svg / mat-icon.
  @Input() public icon: string | undefined;

  // This is used to handle two way binding
  @Input() public inputModel: any;

  @Output() public inputModelChange = new EventEmitter<any>();

  @Output() public onKeyUp: EventEmitter<any> = new EventEmitter<any>();

  @Output() public quit = new EventEmitter<void>();

  // eslint-disable-next-line no-empty-function
  public onChange = (_: any) => {};

  // eslint-disable-next-line no-empty-function
  public onTouched = () => {};

  constructor() {}

  onBlur() {
    this.quit.emit();
  }

  ngOnInit() {
    this.isItPassword = this.type === 'password';
    if (!this.initInput && this.formCtrl) {
      this.inputValue = this.formCtrl.value;
    } else {
      this.inputValue = this.initInput;
    }

    if (this.formCtrl) {
      if (this.disabled) {
        this.formCtrl.disable();
      }

      if (!this.disabled) {
        this.formCtrl.enable();
      }
    }
  }

  writeValue(value: any): void {
    this.inputValue = value;
    this.inputModel = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getIsError() {
    if (this.formCtrl && this.formCtrl.touched && this.formCtrl.errors) {
      return true;
    }

    return false;
  }

  getErrorMessageToShow(): string {
    const errorMessagesArray = Object.entries(this.errorMessagesObject);
    const currentErrorsArray = this.formCtrl?.errors ? Object.entries(this.formCtrl.errors) : [];
    for (const [key, value] of errorMessagesArray) {
      for (const [key2] of currentErrorsArray) {
        if (key === key2) {
          return String(value);
        }
      }
    }
    return '';
  }

  inputModelChangeHandler(): void {
    const output = this.type === 'number' ? Number(this.inputModel) : this.inputModel;
    this.inputModelChange.emit(output);
    this.onChange(output);
    this.onTouched();
  }

  onKeyUpHandler(input: any): void {
    let output: string = String(input);
    if (this.type === 'number') {
      output = input;
      if (this.maxNumber && Number(output) > this.maxNumber) {
        if (this.formCtrl) {
          this.formCtrl.setValue(this.maxNumber);
        }
        output = String(this.maxNumber);
      } else if (this.minNumber && Number(output) < this.minNumber) {
        if (this.formCtrl) {
          this.formCtrl.setValue(this.minNumber);
        }
        output = String(this.minNumber);
      }
    }
    this.inputValue = output;
    this.inputModel = output;
    if (this.type === 'number') {
      this.onKeyUp.emit(Number(output));
    } else {
      this.onKeyUp.emit(output);
    }
    this.inputModelChangeHandler();
  }

  increaseNumber(): void {
    if (this.formCtrl) {
      if (!this.formCtrl.disabled && !this.disabled) {
        if (this.maxNumber !== undefined && this.formCtrl.value < this.maxNumber) {
          this.formCtrl.setValue(Number(this.formCtrl.value) + 1);
          this.inputValue = String(Number(this.formCtrl.value));
          this.inputModelChangeHandler();
        } else if (this.maxNumber === undefined) {
          this.formCtrl.setValue(Number(this.formCtrl.value) + 1);
          this.inputValue = String(Number(this.formCtrl.value));
          this.inputModelChangeHandler();
        }
      }
    } else {
      if (!this.disabled) {
        if (this.maxNumber !== undefined && this.inputModel < this.maxNumber) {
          this.inputModel = Number(this.inputModel) + 1;
          this.inputValue = String(Number(this.inputModel));
          this.inputModelChangeHandler();
        } else if (this.maxNumber === undefined) {
          this.inputModel = Number(this.inputModel) + 1;
          this.inputValue = String(Number(this.inputModel));
          this.inputModelChangeHandler();
        }
      }
    }
  }

  decreaseNumber(): void {
    if (this.formCtrl) {
      if (!this.formCtrl.disabled && !this.disabled) {
        if (this.minNumber !== undefined && this.formCtrl.value > this.minNumber) {
          this.formCtrl.setValue(Number(this.formCtrl.value) - 1);
          this.inputValue = String(Number(this.formCtrl.value));
          this.inputModelChangeHandler();
        } else if (this.minNumber === undefined) {
          this.formCtrl.setValue(Number(this.formCtrl.value) - 1);
          this.inputValue = String(Number(this.formCtrl.value));
          this.inputModelChangeHandler();
        }
      }
    } else {
      if (!this.disabled) {
        if (this.minNumber !== undefined && this.inputModel > this.minNumber) {
          this.inputModel = Number(this.inputModel) - 1;
          this.inputValue = String(Number(this.inputModel));
          this.inputModelChangeHandler();
        } else if (this.minNumber === undefined) {
          this.inputModel = Number(this.inputModel) - 1;
          this.inputValue = String(Number(this.inputModel));
          this.inputModelChangeHandler();
        }
      }
    }
  }

  togglePassword(): void {
    if (this.type === 'text') {
      this.type = 'password';
    } else {
      this.type = 'text';
    }
  }
}
